import { customElement, property } from "lit/decorators.js";
import { html, LitElement } from "lit";

@customElement("ehm-filter-tabs")
export class FilterTabs extends LitElement {
  @property({ type: String }) param = "status";

  private _tabs: HTMLAnchorElement[] = [];

  connectedCallback() {
    super.connectedCallback();
    this._tabs = Array.from(
      this.querySelectorAll<HTMLAnchorElement>("a[role='tab']")
    );
    this._tabs.forEach((tab) => {
      tab.addEventListener("click", this.handleTabClick);
      tab.addEventListener("keydown", this.handleKeyDown);
    });
    window.addEventListener("popstate", this.hydrateFromUrl);
    this.hydrateFromUrl();
  }

  disconnectedCallback() {
    this._tabs.forEach((tab) => {
      tab.removeEventListener("click", this.handleTabClick);
      tab.removeEventListener("keydown", this.handleKeyDown);
    });
    window.removeEventListener("popstate", this.hydrateFromUrl);
    super.disconnectedCallback();
  }

  private handleTabClick = (e: Event) => {
    e.preventDefault();
    const tab = e.currentTarget as HTMLAnchorElement;
    if (tab.getAttribute("aria-selected") === "true") return;
    this.selectTab(tab);
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    const currentIndex = this._tabs.indexOf(
      e.currentTarget as HTMLAnchorElement
    );
    let newIndex = -1;

    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        newIndex = (currentIndex + 1) % this._tabs.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        newIndex =
          (currentIndex - 1 + this._tabs.length) % this._tabs.length;
        break;
      case "Home":
        newIndex = 0;
        break;
      case "End":
        newIndex = this._tabs.length - 1;
        break;
      default:
        return;
    }

    e.preventDefault();
    this._tabs[newIndex].focus();
    this.selectTab(this._tabs[newIndex]);
  };

  private selectTab(tab: HTMLAnchorElement) {
    this._tabs.forEach((t) => {
      t.setAttribute("aria-selected", "false");
      t.classList.remove("active");
      t.setAttribute("tabindex", "-1");
    });

    tab.setAttribute("aria-selected", "true");
    tab.classList.add("active");
    tab.setAttribute("tabindex", "0");

    this.applySearchPlaceholder(tab);

    this.dispatchEvent(
      new CustomEvent("tab-changed", {
        detail: { param: this.param, value: tab.dataset.value },
        bubbles: true,
        composed: true,
      })
    );
  }

  private applySearchPlaceholder(tab: HTMLAnchorElement) {
    const placeholder = tab.dataset.searchPlaceholder;
    if (!placeholder) return;

    const input = this.closest("ehm-filter")?.querySelector<HTMLInputElement>(
      "input[type='search']"
    );
    input?.setAttribute("placeholder", placeholder);
  }

  hydrateFromUrl = () => {
    const url = new URL(window.location.href);
    const currentValue = url.searchParams.get(this.param);

    this._tabs.forEach((tab, i) => {
      const isActive = currentValue
        ? tab.dataset.value === currentValue
        : i === 0;
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
      tab.classList.toggle("active", isActive);
      tab.setAttribute("tabindex", isActive ? "0" : "-1");
      if (isActive) this.applySearchPlaceholder(tab);
    });
  };

  protected render() {
    return html`<slot></slot>`;
  }
}
